import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

// Define the context type
interface AppContextType {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  errorStatus: number | null;
  setErrorStatus: (error: number | null) => void;
  stickyNotification: {
    show: boolean;
    title: string;
    content: string;
    level: string;
    refresh: boolean;
    showInfoIcon: boolean;
    autoFade: boolean;
  };
  headerVersion: string;
  setHeaderVersion: (version: string) => void;
  hideHeaderSteps: boolean;
  setHideHeaderSteps: (hide: boolean) => void;
  hideHeader: boolean;
  setHideHeader: (hide: boolean) => void;
  useWhitePageBackground: boolean;
  setUseWhitePageBackground: (useWhite: boolean) => void;
  hideFooter: boolean;
  setHideFooter: (hide: boolean) => void;
  setStickyNotification: (notification: {
    show: boolean;
    title: string;
    content: string;
    level: string;
    showInfoIcon: boolean;
    autoFade: boolean;
  }) => void;
}

// Create the context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Create the provider
export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoadingState] = useState(true);
  const [errorStatus, setErrorStatusState] = useState<number | null>(null);
  const [headerVersion, setHeaderVersion] = useState<string>('v1');
  const [hideHeaderSteps, setHideHeaderSteps] = useState<boolean>(false);
  const [hideHeader, setHideHeader] = useState<boolean>(false);
  const [useWhitePageBackground, setUseWhitePageBackground] = useState<boolean>(false);
  const [hideFooter, setHideFooter] = useState<boolean>(false);
  const [stickyNotification, setStickyNotificationState] = useState({
    show: false,
    title: '',
    content: '',
    level: 'error',
    refresh: false,
    showInfoIcon: true,
    autoFade: false,
  });

  const setIsLoading = (loading: boolean) => setIsLoadingState(loading); // Accepts a boolean
  const setErrorStatus = (error: number | null) => setErrorStatusState(error); // Accepts error status (number or null)
  const setStickyNotification = (notification: {
    show: boolean;
    level: string;
    showInfoIcon: boolean;
    title: string;
    content: string;
    autoFade: boolean;
  }) => setStickyNotificationState((current) => ({...notification, refresh: !current.refresh})); // Accepts the notification object

  useEffect(() => {
    const showStickyNotification = (event: Event) => setStickyNotification((event as CustomEvent).detail);
    window.addEventListener("sticky-notification", showStickyNotification);
    return () => window.removeEventListener("sticky-notification", showStickyNotification);
  }, []);

  return (
    <AppContext.Provider
      value={{
        isLoading,
        setIsLoading,
        errorStatus,
        setErrorStatus,
        stickyNotification,
        setStickyNotification,
        headerVersion,
        setHeaderVersion,
        hideHeaderSteps,
        setHideHeaderSteps,
        hideHeader,
        setHideHeader,
        useWhitePageBackground,
        setUseWhitePageBackground,
        hideFooter,
        setHideFooter
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the context
export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
